/**
 * Outcome of a platform-admin console domain-wide-delegation check for a
 * customer. Shared by the backend DwdCheckService and the console client so the
 * status vocabulary can't drift between them.
 *
 * - `ok`                 — DWD works and Google's customer id matches ours
 * - `id_mismatch`        — DWD works but Google reports a different customer id
 * - `dwd_not_granted`    — the delegate call was rejected (scopes not authorized)
 * - `delegate_not_found` — no delegate configured, or Google has no such account
 * - `delegate_lacks_admin` — the delegate is real and DWD works, but it lacks the
 *                          admin role to read the directory. Detected
 *                          automatically: a 403 can only arrive AFTER a token
 *                          mint that requested every grant scope, so it is
 *                          provably not a scopes problem, and
 *                          `DwdCheckService.runCheck` classifies the
 *                          "not authorized to access this resource" variant
 *                          here (anything else 403 falls through to `error`
 *                          rather than being asserted as a role problem).
 *                          Also returned when the read SUCCEEDS but the
 *                          delegate's own record says `isAdmin !== true` — a
 *                          non-admin reading itself is not always refused, so
 *                          the 403 alone would let such a delegate through as
 *                          `ok`. Asserting it is FR-3.3 / AC-4.3's permission
 *                          check (EDUS-2683).
 * - `delegate_inactive`  — the delegate exists but is suspended or archived, so
 *                          it cannot act for the tenant. FR-3.3's Authentication
 *                          Check asks for a valid, *active* account; this is the
 *                          "active" half. Kept separate from
 *                          `delegate_not_found` because the account is real and
 *                          the fix is different (restore it, or designate
 *                          another), and separate from `delegate_lacks_admin`
 *                          because an inactive account is unusable whatever its
 *                          role. Only reachable when the delegated read
 *                          SUCCEEDS — Google usually refuses to mint a token for
 *                          an inactive user, and that failure is
 *                          indistinguishable from a missing grant, so it lands
 *                          on `dwd_not_granted` instead.
 * - `error`              — network/timeout/unexpected failure
 */
export const DWD_CHECK_STATUSES = [
  'ok',
  'id_mismatch',
  'dwd_not_granted',
  'delegate_not_found',
  'delegate_lacks_admin',
  'delegate_inactive',
  'error',
] as const;

/** Derived from the array above so a new status can't be added to only one. */
export type DwdCheckStatus = (typeof DWD_CHECK_STATUSES)[number];

/**
 * The DWD statuses an internal QA override may FORCE for a test customer, so QA
 * can reach the Step-3 verification-failure views (delegate_not_found /
 * id_mismatch / generic error) that otherwise need a DB edit + working DWD.
 * `ok` is excluded — real success is easy to reach and forcing it would skip the
 * real provisioning the success path does. Test-customer-only (allowlisted).
 */
export const QA_DWD_OVERRIDE_STATUSES = [
  'dwd_not_granted',
  'delegate_not_found',
  'delegate_lacks_admin',
  'delegate_inactive',
  'id_mismatch',
  'error',
] as const satisfies readonly DwdCheckStatus[];

export type QaDwdOverrideStatus = (typeof QA_DWD_OVERRIDE_STATUSES)[number];
